import { useState } from 'react';
import { GripVertical, Trash2, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ItemBuilder } from './ItemBuilder';
import type { ChecklistSecaoSchema, ChecklistItemSchema } from '@portal-leo/shared';

interface SectionBuilderProps {
  section: ChecklistSecaoSchema;
  onChange: (section: ChecklistSecaoSchema) => void;
  onRemove: () => void;
}

export function SectionBuilder({ section, onChange, onRemove }: SectionBuilderProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleNomeChange = (nome: string) => {
    onChange({ ...section, nome });
  };

  const handleAddItem = () => {
    const newItem: ChecklistItemSchema = {
      nome: 'Novo item',
      tipo: 'CHECKBOX',
      obrigatorio: false,
      ordem: section.itens.length,
      dataPrazo: null,
    };
    onChange({ ...section, itens: [...section.itens, newItem] });
  };

  const handleItemChange = (index: number, item: ChecklistItemSchema) => {
    const newItens = [...section.itens];
    newItens[index] = item;
    onChange({ ...section, itens: newItens });
  };

  const handleItemRemove = (index: number) => {
    onChange({ ...section, itens: section.itens.filter((_, i) => i !== index) });
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-200">
        <GripVertical className="h-4 w-4 text-gray-400 cursor-move flex-shrink-0" />

        <input
          type="text"
          value={section.nome}
          onChange={(e) => handleNomeChange(e.target.value)}
          placeholder="Nome da secao"
          className="flex-1 px-2 py-1 text-sm font-medium border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </Button>

        <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="p-3 space-y-2">
          {section.itens.map((item, index) => (
            <ItemBuilder
              key={item.id || index}
              item={item}
              onChange={(updatedItem) => handleItemChange(index, updatedItem)}
              onRemove={() => handleItemRemove(index)}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="w-full mt-2"
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar campo
          </Button>
        </div>
      )}
    </div>
  );
}
